import React, { useState } from "react";
import { Box, FormHelperText, Grid2, Typography } from "@mui/material";
import {
  CurrentValueBox,
  MAX_REMARK_LENGTH,
  RemarkField,
  StyledTextField,
  ViewHeader,
} from "./EditDialogShared";
import { PrimaryFormLoadingActionButton } from "../../Features/Table/GenericDraggableTable/TableStyles";
import { useToast } from "../../Hooks/useToast";
import { useBatteryInfo, useVehicleInfo } from "../../Pages/DigitalTwinV3/DigitalTwinContext";
import { updateDigitalTwinDetails } from "../../Api/DigitalTwin";
import { translateErrorMessage } from "../../Helper/Common/FieldTranslator";

export default function ChangeLicensePlateView({ onBack, onSuccess }) {
  const { openNotification } = useToast();
  const { batteryID } = useBatteryInfo();
  const { licensePlateNo: currentLicensePlate } = useVehicleInfo();

  const [newLicensePlate, setNewLicensePlate] = useState("");
  const [remark, setRemark] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    const errs = {};
    if (!newLicensePlate.trim()) errs.licensePlate = "Please enter a license plate number";
    if (!remark.trim()) errs.remark = true;
    if (remark.length > MAX_REMARK_LENGTH) errs.remarkCount = true;

    setErrors(errs);
    if (Object.keys(errs).length) return;

    setIsLoading(true);
    try {
      const response = await updateDigitalTwinDetails(batteryID, {
        licensePlateNo: newLicensePlate.trim(),
        remarks: remark,
      });

      if (response?.responseStatus?.code !== 200) {
        throw new Error(response?.responseStatus?.msg);
      }

      await onSuccess("License Plate Number Updated!");
    } catch (error) {
      const msg = translateErrorMessage(error?.message);
      setErrors({ licensePlate: msg });
      openNotification({ text: msg, icon: "toast-error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <ViewHeader title="Change License Plate Number" onBack={onBack} />

      <CurrentValueBox label="Current License Plate Number" value={currentLicensePlate} />

      <Grid2 container flexDirection="column" mb={1.5} gap={0.5}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            variant="fw400s14c000000"
            sx={{ lineHeight: "24px", color: "#494949" }}
          >
            New License Plate Number
          </Typography>
          <Typography
            variant="fw400s14c000000"
            sx={{ fontWeight: 700, lineHeight: "24px", color: "#B50500" }}
          >
            &nbsp;*
          </Typography>
        </Box>
        <StyledTextField
          fullWidth
          autoComplete="off"
          variant="outlined"
          placeholder="Type here"
          value={newLicensePlate}
          onChange={(e) => setNewLicensePlate(e.target.value)}
          error={!!errors.licensePlate}
        />
        {errors.licensePlate && (
          <FormHelperText sx={{ fontSize: "10px", color: "#930500", ml: 0, mt: -0.5 }}>
            {errors.licensePlate}
          </FormHelperText>
        )}
      </Grid2>

      <RemarkField
        value={remark}
        onChange={(e) => setRemark(e.target.value)}
        error={errors.remark}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <PrimaryFormLoadingActionButton
          disabled={isLoading}
          onClick={handleSubmit}
          loading={isLoading}
          sx={{ borderRadius: "4px", px: 3 }}
        >
          Confirm Change
        </PrimaryFormLoadingActionButton>
      </Box>
    </Box>
  );
}
